import { forwardRef, useCallback, useEffect, useRef, type InputHTMLAttributes, type PointerEvent, type Ref } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { controlSurface } from './control-styles';

const LEFT_TO_RIGHT_TYPES = new Set(['tel', 'email', 'number', 'url']);
const WRAPPER_CLASS = /^-?(w|min-w|max-w|basis|m|mx|my|mt|mb|ms|me|ml|mr|col-span|col-start|col-end|row-span|self|justify-self|order)-|^(flex-1|flex-auto|flex-none|grow|grow-0|shrink|shrink-0|hidden|block|inline-block)$/;
const HOLD_DELAY_MS = 400;
const HOLD_REPEAT_MS = 70;

type InputProps = InputHTMLAttributes<HTMLInputElement>;

const inputClasses = (className?: string) =>
  cn(
    controlSurface,
    'flex h-9 px-3 py-1',
    'file:me-3 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
    className,
  );

function splitLayoutClasses(className = '') {
  const wrapper: string[] = [];
  const input: string[] = [];
  for (const token of className.split(/\s+/).filter(Boolean)) {
    const base = token.slice(token.lastIndexOf(':') + 1);
    (WRAPPER_CLASS.test(base) ? wrapper : input).push(token);
  }
  return { wrapper: wrapper.join(' '), input: input.join(' ') };
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

function nudge(input: HTMLInputElement, direction: 1 | -1) {
  const before = input.value;
  try {
    if (direction === 1) input.stepUp();
    else input.stepDown();
  } catch {
    const next = (Number(input.value) || 0) + direction;
    const min = input.min === '' ? -Infinity : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    input.value = String(Math.min(max, Math.max(min, next)));
  }
  if (input.value === before) return false;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

const NumberInput = forwardRef<HTMLInputElement, InputProps>(({ className, dir, ...props }, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timers = useRef<{ delay?: number; repeat?: number }>({});
  const { wrapper, input } = splitLayoutClasses(className);
  const showStepper = !props.disabled && !props.readOnly;

  const stop = useCallback(() => {
    window.clearTimeout(timers.current.delay);
    window.clearInterval(timers.current.repeat);
    timers.current = {};
  }, []);

  useEffect(() => stop, [stop]);

  const start = (direction: 1 | -1) => (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const node = inputRef.current;
    if (!node) return;
    node.focus();
    stop();
    nudge(node, direction);
    timers.current.delay = window.setTimeout(() => {
      timers.current.repeat = window.setInterval(() => {
        if (!nudge(node, direction)) stop();
      }, HOLD_REPEAT_MS);
    }, HOLD_DELAY_MS);
  };

  const stepButton = 'grid flex-1 place-items-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-primary/10 active:text-primary';

  return (
    <div dir={dir ?? 'ltr'} className={cn('number-field group/number relative w-full', wrapper)}>
      <input
        ref={(node) => {
          inputRef.current = node;
          assignRef(ref, node);
        }}
        type="number"
        dir={dir ?? 'ltr'}
        className={inputClasses(cn(showStepper && 'pe-8', input))}
        {...props}
      />
      {showStepper && (
        <div
          aria-hidden
          className="number-stepper absolute inset-y-px end-px flex w-6 flex-col overflow-hidden rounded-e-[5px] border-s border-input bg-muted/30 opacity-70 transition-opacity group-hover/number:opacity-100 group-focus-within/number:opacity-100"
        >
          <button type="button" tabIndex={-1} className={stepButton} onPointerDown={start(1)} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}>
            <ChevronUp className="h-3 w-3" strokeWidth={2.5} />
          </button>
          <span className="h-px bg-input" />
          <button type="button" tabIndex={-1} className={stepButton} onPointerDown={start(-1)} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}>
            <ChevronDown className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
});
NumberInput.displayName = 'NumberInput';

export const Input = forwardRef<HTMLInputElement, InputProps>(({ className, ...props }, ref) => {
  if (props.type === 'number') return <NumberInput ref={ref} className={className} {...props} />;
  return (
    <input
      ref={ref}
      dir={props.dir ?? (props.type && LEFT_TO_RIGHT_TYPES.has(props.type) ? 'ltr' : undefined)}
      className={inputClasses(className)}
      {...props}
    />
  );
});
Input.displayName = 'Input';
